import { TSupplierPermission, createSupplierPermission, updateSupplierPermission } from "@/api";
import { useAdmin } from "@/hooks/admin";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Typography, Button } from "@mui/material";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { SaveButton, SimpleForm, TextInput, Toolbar, useNotify, useRedirect, useRecordContext } from "react-admin";
import { useFormContext } from "react-hook-form";
import * as yup from "yup";
import { useGroupTranslates, useSupplierPermissionTranslations } from "@/hooks/translates";

type TSupplierPermissionForm = {
  isEdit?: boolean;
};

const SupplierPermissionToolbar = () => (
  <Toolbar>
    <SaveButton />
  </Toolbar>
);

const coerceString = yup
  .string()
  .transform((val) => (val == null ? "" : val));

const schemaSupplierPermissionForm = yup.object().shape({
  key: coerceString.required(),
  nameEs: coerceString.required(),
  descriptionEs: coerceString.required(),
  nameEn: coerceString.required(),
  descriptionEn: coerceString.required(),
});

const supplierPermissionDefaultValues = () => ({
  key: "",
  nameEs: "",
  descriptionEs: "",
  nameEn: "",
  descriptionEn: "",
});

const TranslationsInitializer: FC = () => {
  const record = useRecordContext<TSupplierPermission>();
  const { setValue, getValues, formState } = useFormContext();
  const { values: tValues, isLoading } = useSupplierPermissionTranslations(record?.key);
  useEffect(() => {
    if (isLoading) return;
    if (!formState.dirtyFields?.nameEs && !getValues("nameEs")) {
      setValue("nameEs", tValues.nameEs, { shouldDirty: false });
    }
    if (!formState.dirtyFields?.nameEn && !getValues("nameEn")) {
      setValue("nameEn", tValues.nameEn, { shouldDirty: false });
    }
    if (!formState.dirtyFields?.descriptionEs && !getValues("descriptionEs")) {
      setValue("descriptionEs", tValues.descriptionEs, { shouldDirty: false });
    }
    if (!formState.dirtyFields?.descriptionEn && !getValues("descriptionEn")) {
      setValue("descriptionEn", tValues.descriptionEn, { shouldDirty: false });
    }
  }, [isLoading, tValues, formState.dirtyFields, getValues, setValue]);

  return null;
};

export const SupplierPermissionForm: FC<TSupplierPermissionForm> = ({
  isEdit,
}) => {
  const { hasPermission } = useAdmin();
  const notify = useNotify();
  const redirect = useRedirect();
  const { upsertTranslate } = useGroupTranslates("supplier-permissions");

  const handleSubmit = useCallback(
    async (values: any) => {
      try {
        if (isEdit) {
          await updateSupplierPermission(values.id, {
            key: values.key,
            name: values.nameEs,
            description: values.descriptionEs          
          });

          await Promise.all([
            upsertTranslate(`${values.key}.name`, "es", values.nameEs),
            upsertTranslate(`${values.key}.name`, "en", values.nameEn),
            upsertTranslate(`${values.key}.description`, "es", values.descriptionEs),
            upsertTranslate(`${values.key}.description`, "en", values.descriptionEn),
          ]);

          notify("Updated supplier permission", { type: "info" });
          redirect("show", "supplier-permissions", values.id);
          return;
        }

        const created = await createSupplierPermission({
          key: values.key,
          name: values.nameEs,
          description: values.descriptionEs,
        });

        await Promise.all([
          upsertTranslate(`${values.key}.name`, "es", values.nameEs),
          upsertTranslate(`${values.key}.name`, "en", values.nameEn),
          upsertTranslate(`${values.key}.description`, "es", values.descriptionEs),
          upsertTranslate(`${values.key}.description`, "en", values.descriptionEn),
        ]);

        notify("Created supplier permission", { type: "info" });
        redirect("show", "supplier-permissions", created.id);
      } catch (e: any) {
        notify(e?.message ?? "Failed to save supplier permission", { type: "warning" });
      }
    },
    [isEdit, notify, redirect]
  );

  if (!hasPermission("supplier-permission.create")) return null;

  return (
    <SimpleForm
      mode="onBlur"
      toolbar={<SupplierPermissionToolbar />}
      defaultValues={supplierPermissionDefaultValues()}
      resolver={yupResolver(schemaSupplierPermissionForm)}
      sanitizeEmptyValues={false}
      onSubmit={handleSubmit}
      warnWhenUnsavedChanges
    >
      {isEdit && <TranslationsInitializer />}
      <Grid container spacing={4}>
        {isEdit && (
          <Grid item xs={4}>
            <TextInput source="id" disabled fullWidth />
          </Grid>
        )}
        <Grid item xs={4}>
          <TextInput source="key" fullWidth />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle1">Español (es)</Typography>
        </Grid>
        <Grid item xs={6}>
          <TextInput source="nameEs" label="Name (es)" fullWidth />
        </Grid>
        <Grid item xs={6}>
          <TextInput source="descriptionEs" label="Description (es)" fullWidth multiline />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <AutoTranslateButton />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle1">Inglés (en)</Typography>
        </Grid>
        <Grid item xs={6}>
          <TextInput source="nameEn" label="Name (en)" fullWidth />
        </Grid>
        <Grid item xs={6}>
          <TextInput source="descriptionEn" label="Description (en)" fullWidth multiline />
        </Grid>
      </Grid>
    </SimpleForm>
  );
};

const AutoTranslateButton: FC = () => {
  const { getValues, setValue, watch, trigger } = useFormContext();
  const notify = useNotify();
  const hasTranslator = typeof (self as any).Translator !== "undefined";
  const [loading, setLoading] = useState(false);

  const [nameEs, descriptionEs] = watch(["nameEs", "descriptionEs"]);
  const hasSource = useMemo(() => {
    const n = (nameEs ?? "").trim();
    const d = (descriptionEs ?? "").trim();
    return Boolean(n || d);
  }, [nameEs, descriptionEs]);

  const onClick = useCallback(async () => {
    try {
      setLoading(true);
      const TranslatorAPI = (self as any).Translator as {
        create: (opts: { sourceLanguage: string; targetLanguage: string }) => Promise<{
          translate: (text: string) => Promise<string>;
        }>;
      };

      const values = getValues();
      const sourceName: string = values?.nameEs ?? "";
      const sourceDesc: string = values?.descriptionEs ?? "";
      if (!sourceName && !sourceDesc) {
        notify("Nada que traducir desde español", { type: "warning" });
        return;
      }

      const translator = await TranslatorAPI.create({ sourceLanguage: "es", targetLanguage: "en" });
      const [translatedName, translatedDesc] = await Promise.all([
        sourceName ? translator.translate(sourceName) : Promise.resolve(""),
        sourceDesc ? translator.translate(sourceDesc) : Promise.resolve("")
      ]);

      if (translatedName) setValue("nameEn", translatedName, { shouldDirty: true });
      if (translatedDesc) setValue("descriptionEn", translatedDesc, { shouldDirty: true });
      await trigger(["nameEn", "descriptionEn"]);
      notify("Traducciones generadas", { type: "info" });
    } catch (e) {
      notify("Error al generar traducciones", { type: "warning" });
    } finally {
      setLoading(false);
    }
  }, [getValues, notify, setValue, trigger]);

  if (!hasTranslator) return null;
  return (
    <Button variant="outlined" onClick={onClick} disabled={!hasSource || loading}>
      {loading ? "Traduciendo…" : "Auto-traducir al inglés"}
    </Button>
  );
};
